import { IonButton } from '@ionic/react';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useGetUserLazyQuery } from '../../api/graphql/generated';
import { useUserProvider } from '../../api/user.provider';
import { SkeletonPage } from '../../components/skeleton/skeleton-page';
import { ApplicationConfig } from '../../config/config';
import { runInBackground } from '../../util/async';
import { logger } from '../../util/logger';
import { DirectConnectUserSelectModal } from '../component/direct-connect-user-select';
import { useDirectConnectGoBack } from '../helper/direct-connect-helper';
import { DirectConnectHealthUserInfo } from '../model/direct-connect.model';
import { useDirectConnectHealthGateContext } from './direct-connect-health-gate';

export interface DirectConnectUserGateProps {
  children: ReactNode;
}

interface DirectConnectUserGateContextValue {
  user?: DirectConnectHealthUserInfo;
  switchUser: () => void;
}

const DirectConnectUserGateContext = createContext<
  DirectConnectUserGateContextValue | undefined
>(undefined);

const DirectConnectUserGate = (props: DirectConnectUserGateProps) => {
  const { children } = props;

  return (
    <DirectConnectUserGateInternal>{children}</DirectConnectUserGateInternal>
  );
};

const DirectConnectUserGateInternal = (props: DirectConnectUserGateProps) => {
  const { children } = props;

  const goBack = useDirectConnectGoBack();
  const { i18n } = useTranslation();
  const { userInfo, setUserInfo } = useUserProvider();
  const { healthStatus } = useDirectConnectHealthGateContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [directConnectUser, setDirectConnectUser] = useState<
    DirectConnectHealthUserInfo | undefined
  >();

  const [hasRenderedChildrenOnce, setHasRenderedChildrenOnce] = useState(false);

  const showChildren =
    userInfo?.userId !== undefined || hasRenderedChildrenOnce;

  const switchUser = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const [getUserLazy] = useGetUserLazyQuery();

  const onSelectUser = useCallback(
    async (user?: DirectConnectHealthUserInfo) => {
      if (user?.userId && healthStatus?.machine?.tenantId) {
        setIsModalOpen(false);

        setDirectConnectUser(user);

        // we need to manually query for the user to hydrate the cache since
        // we are in direct connect mode. underlying user hooks
        // will default to cache-only
        await getUserLazy({
          variables: {
            input: {
              id: user.userId,
            },
          },
          fetchPolicy: 'network-only',
        });

        setUserInfo({
          userId: user.userId,
          tenantId: healthStatus.machine.tenantId,
          tenantName: healthStatus.tenant?.tenantName,
        });
      }
    },
    [healthStatus, setUserInfo, getUserLazy]
  );

  useEffect(() => {
    if (showChildren && !hasRenderedChildrenOnce) {
      setHasRenderedChildrenOnce(true);
    }
  }, [showChildren, hasRenderedChildrenOnce]);

  useEffect(() => {
    if (!userInfo?.userId) {
      setIsModalOpen(true);
    }
  }, [userInfo]);

  const contextValue = useMemo(() => {
    return {
      switchUser,
      user: directConnectUser,
    };
  }, [switchUser, directConnectUser]);

  const modalActionButton = useMemo(() => {
    // if we have a user then we should show a cancel button to dismiss
    // the modal since we are switching users. otherwise we should show
    // a back button and navigate back to the previous page

    if (userInfo?.userId) {
      return (
        <IonButton
          onClick={() => {
            setIsModalOpen(false);
          }}
        >
          {i18n.t('common:cancel')}
        </IonButton>
      );
      // TODO remove disconnect button altogether once we have WiFI based
      // Direct Connect customers running PolyscopeX
      // don't show disconnect button if we are in kiosk mode
    } else if (!ApplicationConfig.kiosk.enabled) {
      return (
        <IonButton
          onClick={() => {
            runInBackground(async () => {
              goBack();
            });
          }}
        >
          {i18n.t('common:disconnect')}
        </IonButton>
      );
    }
  }, [userInfo, i18n, goBack]);

  useEffect(() => {
    logger.info('direct connect user gate', {
      showChildren,
      hasRenderedChildrenOnce,
    });
  }, [showChildren, hasRenderedChildrenOnce]);

  return (
    <DirectConnectUserGateContext.Provider value={contextValue}>
      {showChildren && children}
      {!showChildren && <SkeletonPage />}
      <DirectConnectUserSelectModal
        isOpen={isModalOpen}
        onSelect={onSelectUser}
        leftButtons={modalActionButton}
      />
    </DirectConnectUserGateContext.Provider>
  );
};

const useDirectConnectUserGateContext = () => {
  return useContext(
    DirectConnectUserGateContext
  ) as DirectConnectUserGateContextValue;
};

export { DirectConnectUserGate, useDirectConnectUserGateContext };
